import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BlockModel } from '../../../../PageBuilder/models/block.model';
import { LayoutBlock } from '../../../../PageBuilder/models/layout-block.model';
import { ColorModel } from '../../../models/color.model';


export const MAX_RECENT_COLORS = 10;

export enum ColorProperty {
    // text
    TEXT_COLOR = 'color',
    // background
    BACKGROUND_COLOR = 'backgroundColor',
    // accent
    ACCENT_COLOR = 'accentColor',
    // divider
    DIVIDER_COLOR = 'dividerColor',
    // image
    BORDER_COLOR = 'borderColor',
    // icon
    ICON_COLOR = 'iconColor',
    ICON_BACKGROUND_COLOR = 'iconBackgroundColor',
    // button styles
    BUTTON_TEXT_COLOR = 'fontColor',
    BUTTON_BACKGROUND_COLOR = 'buttonColor',
    BUTTON_BLOCK_BACKGROUND_COLOR = 'buttonBackgroundColor',
};

export enum ColorType {
    ACCENT = 'ACCENT',
    TEXT = 'TEXT',
    BACKGROUND = 'BACKGROUND',
    BORDER = 'BORDER',
    DIVIDER = 'DIVIDER',
    BUTTON = 'BUTTON',
    OTHER = 'OTHER',
    CURRENT = 'CURRENT'
};

/**
 * Color properties that can be found in block styles
 */
const COLOR_PROPERTIES: ColorProperty[] = [
    ColorProperty.TEXT_COLOR,
    ColorProperty.BACKGROUND_COLOR,
    ColorProperty.ACCENT_COLOR,
    ColorProperty.DIVIDER_COLOR,
    ColorProperty.BORDER_COLOR,
    ColorProperty.ICON_COLOR,
    ColorProperty.ICON_BACKGROUND_COLOR,
    ColorProperty.BUTTON_TEXT_COLOR,
    ColorProperty.BUTTON_BACKGROUND_COLOR,
    ColorProperty.BUTTON_BLOCK_BACKGROUND_COLOR,
];

/**
 * Nested style objects that may contain color properties
 */
const NESTED_STYLE_PROPERTIES = [
    'font',
    'heading',
    'headingTwo',
    'headingThree',
    'sectionHeading',
    'snippetFont',
    'insightsFont',
    'metadataFont',
    'notesFont',
    'mentionInsightsLinkFont'
];

const COLOR_PROPERTY_TO_COLOR_TYPE: Record<ColorProperty, ColorType> = {
    [ColorProperty.TEXT_COLOR]: ColorType.TEXT,
    [ColorProperty.BACKGROUND_COLOR]: ColorType.BACKGROUND,
    [ColorProperty.ACCENT_COLOR]: ColorType.ACCENT,
    [ColorProperty.DIVIDER_COLOR]: ColorType.DIVIDER,
    [ColorProperty.BORDER_COLOR]: ColorType.BORDER,
    [ColorProperty.ICON_COLOR]: ColorType.TEXT,
    [ColorProperty.ICON_BACKGROUND_COLOR]: ColorType.BACKGROUND,
    [ColorProperty.BUTTON_TEXT_COLOR]: ColorType.TEXT,
    [ColorProperty.BUTTON_BACKGROUND_COLOR]: ColorType.BUTTON,
    [ColorProperty.BUTTON_BLOCK_BACKGROUND_COLOR]: ColorType.BACKGROUND,
};

interface ExtractedColor {
    color: string;
    type: ColorType;
    count: number;
}

interface GeneralStyles {
    [ColorProperty.ACCENT_COLOR]?: string;
    [key: string]: string | Record<string, unknown> | undefined;
}

/**
 * Minimal shape of a chart's annotations, described locally so this file doesn't need to
 * import the full Reports/PageBuilder chart model chain just to read colors off it
 */
interface ChartAnnotationsLike {
    markerState: any;
}

interface BlockDisplayElementLike {
    chartAnnotations?: ChartAnnotationsLike;
    chartBlock?: {
        displayElement?: {
            chartAnnotations?: ChartAnnotationsLike;
        };
    };
}

interface RecentColorsContext {
    blocks: BlockModel[];
    generalStyles: any;
}

@Injectable({ providedIn: 'root' })
export class RecentColorsService {
    private context$: BehaviorSubject<RecentColorsContext | null> = new BehaviorSubject<RecentColorsContext | null>(null);

    setContext(blocks: BlockModel[], generalStyles: any): void {
        this.context$.next({ blocks, generalStyles });
    }

    getRecentColors(property: ColorProperty | string, currentColor?: string): Observable<string[]> {
        return this.context$.pipe(
            filter(context => !!context),
            map(context => this.extractColors(context.blocks, context.generalStyles, property, currentColor))
        );
    }

    /**
     * Extract unique colors from blocks, prioritized by usage
     * @param blocksOwner The BlockModel to extract colors from
     * @param preferredType Optional - prioritize colors of this type
     * @returns Array of hex color strings (max 10)
     */
    public extractColors(blocks: BlockModel[], generalStyles: GeneralStyles, property: ColorProperty | string, currentColor?: string): string[] {
        if (!blocks) {
            return [];
        }

        const colorMap = new Map<string, ExtractedColor>();

        // Extract accecnt color from general styles first
        this.extractAccentColors(generalStyles, colorMap);

        // Extract colors from all blocks
        this.extractColorsFromBlocks(blocks, colorMap);


        // Extract colors from general styles
        this.extractColorsFromGeneralStyles(generalStyles, colorMap);

        // current color always comes after accect
        if (currentColor) {
            const existing = colorMap.get(currentColor);

            if (existing) {
                existing.type = ColorType.CURRENT;
            } else {
                this.addColor(colorMap, currentColor, ColorType.CURRENT);
            }
        }

        // Convert map to array and sort by priority, then by count (most used first)
        const colors = Array.from(colorMap.values());
        const preferredType = COLOR_PROPERTY_TO_COLOR_TYPE[property as ColorProperty];

        // Sort colors: accent colors first, then preferred type
        colors.sort((a, b) => {
            // 1. Accent colors first
            if (a.type === ColorType.ACCENT && b.type !== ColorType.ACCENT) {
                return -1;
            };
            if (b.type === ColorType.ACCENT && a.type !== ColorType.ACCENT) {
                return 1;
            };

            // 2. currentColor type next
            if (currentColor) {
                if (a.type === ColorType.CURRENT && b.type !== ColorType.CURRENT) {
                    return -1;
                };
                if (b.type === ColorType.CURRENT && a.type !== ColorType.CURRENT) {
                    return 1;
                };
            }

            // 3. Preferred type next
            if (preferredType) {
                if (a.type === preferredType && b.type !== preferredType) {
                    return -1;
                };
                if (b.type === preferredType && a.type !== preferredType) {
                    return 1;
                };
            }

            // 4. Then by usage count (descending)
            return b.count - a.count;
        });

        return colors.slice(0, MAX_RECENT_COLORS).map(c => c.color);
    }


    private extractAccentColors(generalStyles: GeneralStyles, colorMap: Map<string, ExtractedColor>) {
        const accentColor = generalStyles?.accentColor as string;
        if (accentColor) {
            this.addColor(colorMap, accentColor, ColorType.ACCENT);
        }
    }

    /**
     * Extract unique colors from blocks from general styles
     * @param generalStyles
     * @param colorMap
     */
    private extractColorsFromGeneralStyles(generalStyles: GeneralStyles, colorMap: Map<string, ExtractedColor>) {
        Object.values(generalStyles).forEach((values) => {
            if (!values || typeof values !== 'object') {
                return;
            };
            COLOR_PROPERTIES.forEach(prop => {
                const color = values[prop];
                if (color && typeof color === 'string') {
                    const colorType = this.getColorType(prop);
                    this.addColor(colorMap, color, colorType);
                }
            });
        });
    }

    private extractColorsFromBlocks(blocks: BlockModel[], colorMap: Map<string, ExtractedColor>): void {
        if (!blocks) {
            return;
        }

        blocks.forEach(block => {
            // Handle layout blocks (they contain nested columns of blocks)
            if (block.isLayoutBlockType && block.isLayoutBlockType()) {
                const layoutBlock = block as LayoutBlock;
                if (layoutBlock.blocks) {
                    layoutBlock.blocks.forEach(column => {
                        this.extractColorsFromBlocks(column, colorMap);
                    });
                }
            }

            // Extract colors from the block's displayElement styles
            if (block.displayElement?.styles) {
                this.extractColorsFromStyles(block.displayElement.styles, colorMap);
            }

            const chartAnnotations = (block.displayElement as BlockDisplayElementLike)?.chartAnnotations;
            if (chartAnnotations) {
                this.extractColorsFromChartAnnotations(chartAnnotations.markerState, colorMap);
            }
            const briefingChartAnnotations = (block.displayElement as BlockDisplayElementLike)?.chartBlock?.displayElement?.chartAnnotations;
            if (briefingChartAnnotations) {
                this.extractColorsFromChartAnnotations(briefingChartAnnotations.markerState, colorMap);
            }
        });
    }

    private extractColorsFromStyles(styles: any, colorMap: Map<string, ExtractedColor>): void {
        if (!styles) {
            return;
        }

        // Extract direct color properties
        COLOR_PROPERTIES.forEach(prop => {
            if (styles[prop]) {
                const colorType = this.getColorType(prop);
                this.addColor(colorMap, styles[prop], colorType);
            }
        });

        // Extract colors from nested style objects (like font settings)
        NESTED_STYLE_PROPERTIES.forEach(nestedProp => {
            if (styles[nestedProp] && typeof styles[nestedProp] === 'object') {
                COLOR_PROPERTIES.forEach(colorProp => {
                    if (styles[nestedProp][colorProp]) {
                        const colorType = this.getColorType(colorProp);
                        this.addColor(colorMap, styles[nestedProp][colorProp], colorType);
                    }
                });
            }
        });

        // Handle colorMap property (used in some block types)
        if (styles.colorMap && typeof styles.colorMap === 'object') {
            Object.values(styles.colorMap).forEach((color) => {
                if (color && typeof color === 'string') {
                    this.addColor(colorMap, color, ColorType.OTHER);
                }
            });
        }
    }

    private extractColorsFromChartAnnotations(markerState: any, colorMap: Map<string, ExtractedColor>): void {
        if (!markerState) {
            return;
        }

        markerState?.markers?.forEach((marker: any) => {
            for (const [key, value] of Object.entries(marker)) {
                if (key.toLocaleLowerCase().includes('color')) {
                    this.addColor(colorMap, value as string, ColorType.OTHER);
                }
            }
        });
    }

    private addColor(colorMap: Map<string, ExtractedColor>, color: string, type: ColorType): void {
        if (!color || typeof color !== 'string') {
            return;
        }

        // Normalize the color to uppercase hex
        const normalizedColor = this.normalizeColor(color);

        if (!normalizedColor || !ColorModel.isValidHex(normalizedColor)) {
            return;
        }

        const existing = colorMap.get(normalizedColor);
        if (existing) {
            existing.count++;
            // Keep the more specific type (TEXT > OTHER, ACCENT > OTHER, etc.)
            if (type !== ColorType.OTHER && existing.type === ColorType.OTHER) {
                existing.type = type;
            }
        } else {
            colorMap.set(normalizedColor, {
                color: normalizedColor,
                type,
                count: 1
            });
        }
    }

    private normalizeColor(color: string): string {
        if (!color) {
            return '';
        }

        let normalized = color.trim().toUpperCase();

        // Ensure it starts with #
        if (!normalized.startsWith('#')) {
            normalized = '#' + normalized;
        }

        // Expand shorthand hex (e.g., #FFF -> #FFFFFF)
        if (normalized.length === 4) {
            normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
        }

        return normalized;
    }

    private getColorType(property: ColorProperty): ColorType {
        return COLOR_PROPERTY_TO_COLOR_TYPE[property] ?? ColorType.OTHER;
    }
}
